/* Hallmark · build · PathwayGeneratorModal · genre: modern-minimal · component-scope
 * theme: Cobalt (project-locked) · pre-emit critique: P5 H5 E5 S5 R5 V5
 * states: default · hover · focus-visible · active · disabled · ready
 * Standards: ANSI/TIA-942-C, TIA-569-D, BICSI 002-2021.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Route,
  X,
  Check,
  Layers,
  Sliders,
  CheckCircle2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { useEditorStore, PPM } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import {
  getAllPathwayPresets,
  type PathwayPresetId,
  type PathwayAlignment,
  type SpineCollectorPosition,
} from '../../utils/pathwayPresets.logic';
import {
  generatePathways,
  type PathwayGeneratorResult,
} from '../../utils/pathwayGenerator.logic';

interface Props {
  onClose: () => void;
}

const PRESET_ICONS: Record<PathwayPresetId, string> = {
  single_overhead: '🪜',
  dual_split: '⚡',
  triple_tier: '🏢',
  underfloor_power: '🧊',
  custom: '🛠️',
};

export const PathwayGeneratorModal: React.FC<Props> = ({ onClose }) => {
  const {
    racks,
    rows,
    trayEdges,
    applyGeneratedPathwaysBatch,
  } = useEditorStore(useShallow(state => ({
    racks: state.racks,
    rows: state.rows,
    trayEdges: state.trayEdges,
    applyGeneratedPathwaysBatch: state.applyGeneratedPathwaysBatch,
  })));

  // Form State
  const [selectedPresetId, setSelectedPresetId] = useState<PathwayPresetId>('dual_split');
  const [spineCollector, setSpineCollector] = useState<SpineCollectorPosition>('head');
  const [alignment, setAlignment] = useState<PathwayAlignment>('rear');
  const [overhangCm, setOverhangCm] = useState<number>(30);
  const [replaceExisting, setReplaceExisting] = useState<boolean>(true);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]); // empty = all rows

  const presets = useMemo(() => getAllPathwayPresets(), []);
  const selectedPreset = useMemo(() => presets.find(p => p.id === selectedPresetId) || presets[0], [presets, selectedPresetId]);

  const existingTrayCount = Object.keys(trayEdges).length;
  const rackList = useMemo(() => Object.values(racks), [racks]);
  const rowList = useMemo(() => Object.values(rows), [rows]);

  // Live Synthesis Preview
  const preview: PathwayGeneratorResult | null = useMemo(() => {
    if (rackList.length === 0) return null;
    try {
      return generatePathways({
        racks: rackList,
        rows,
        presetId: selectedPresetId,
        alignment,
        overhangM: (Number.isFinite(overhangCm) ? Math.max(0, overhangCm) : 30) / 100,
        spineCollector,
        rowIds: selectedRowIds.length > 0 ? selectedRowIds : undefined,
        ppm: PPM,
      });
    } catch {
      return null;
    }
  }, [rackList, rows, selectedPresetId, alignment, overhangCm, spineCollector, selectedRowIds]);

  const handleApply = useCallback(() => {
    if (!preview) return;

    applyGeneratedPathwaysBatch({
      nodes: preview.nodes,
      edges: preview.edges,
      replaceExisting,
    });
    onClose();
  }, [preview, applyGeneratedPathwaysBatch, replaceExisting, onClose]);

  const handleRowToggle = (rId: string) => {
    setSelectedRowIds(prev => {
      if (prev.includes(rId)) {
        return prev.filter(id => id !== rId);
      } else {
        return [...prev, rId];
      }
    });
  };

  // Keyboard accessibility: Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pathway-generator-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2, 6, 23, 0.75)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(8px, 2vw, 16px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-panel, #0b1329)',
          border: '1px solid var(--border, #1e293b)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 680,
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05)',
          overflow: 'hidden',
          color: 'var(--text-primary, #f8fafc)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(to right, rgba(14, 165, 233, 0.08), transparent)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: 'rgba(14, 165, 233, 0.15)',
                border: '1px solid rgba(14, 165, 233, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38bdf8',
                flexShrink: 0,
              }}
            >
              <Route size={18} />
            </div>
            <div>
              <h2
                id="pathway-generator-title"
                style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#f8fafc', letterSpacing: '-0.01em' }}
              >
                Мастер кабельных трасс и магистралей
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-secondary, #94a3b8)' }}>
                Автоматический синтез 3D-графа лотков ЦОД по стандартам ANSI/TIA-942-C и TIA-569
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Закрыть модальное окно"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted, #64748b)',
              cursor: 'pointer',
              padding: 8,
              minWidth: 36,
              minHeight: 36,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.12s ease, color 0.12s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.color = '#f8fafc';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Alert if no racks exist */}
          {rackList.length === 0 && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 8,
                padding: '12px 14px',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                color: '#f87171',
                fontSize: 12,
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong style={{ display: 'block', marginBottom: 2 }}>В зале пока нет стоек</strong>
                Для автоматической генерации трасс сначала добавьте ряды стоек с помощью кнопки «Ряд» (R) или «Матрица» (W).
              </div>
            </div>
          )}

          {/* Section 1: Presets Selection */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Layers size={14} color="#38bdf8" />
                Архитектурный пресет раскладки
              </label>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                {selectedPreset.standard}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
              {presets.map(p => {
                const isSel = p.id === selectedPresetId;
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPresetId(p.id)}
                    style={{
                      border: isSel ? '1.5px solid #0ea5e9' : '1px solid var(--border, #1e293b)',
                      background: isSel ? 'rgba(14, 165, 233, 0.1)' : 'var(--bg-card, #0f172a)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      position: 'relative',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>{PRESET_ICONS[p.id]}</span>
                        {isSel && (
                          <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={10} color="#fff" />
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: isSel ? '#38bdf8' : '#f1f5f9', marginBottom: 3 }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.35 }}>
                        {p.description}
                      </div>
                    </div>

                    {/* Layer Badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                      {p.layers.map(layer => (
                        <span
                          key={layer.id}
                          style={{
                            fontSize: 9,
                            padding: '1px 5px',
                            borderRadius: 4,
                            background: 'rgba(255, 255, 255, 0.06)',
                            border: `1px solid ${layer.color}40`,
                            color: layer.color,
                            fontWeight: 500,
                          }}
                        >
                          {layer.media === 'fiber' ? 'Оптика' : layer.media === 'copper' ? 'Медь' : 'Сила'} {layer.elevationM > 0 ? `+${layer.elevationM}м` : `${layer.elevationM}м`}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Geometry & Routing Parameters */}
          <div
            style={{
              background: 'var(--bg-card, #0f172a)',
              border: '1px solid var(--border, #1e293b)',
              borderRadius: 8,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sliders size={13} color="#38bdf8" />
              Геометрия и магистральные коллекторы
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Spine Collector */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', display: 'block', marginBottom: 5 }}>
                  Поперечный коллектор (Spine):
                </label>
                <select
                  value={spineCollector}
                  onChange={e => setSpineCollector(e.target.value as SpineCollectorPosition)}
                  style={{
                    background: 'var(--bg-panel, #0b1329)',
                    border: '1px solid var(--border-color, #334155)',
                    borderRadius: 6,
                    color: '#f8fafc',
                    padding: '8px 10px',
                    minHeight: 38,
                    fontSize: 11,
                    width: '100%',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="head">Голова рядов (Head Collector)</option>
                  <option value="end">Хвост рядов (End Collector)</option>
                  <option value="both">Оба торца (Both — кольцо)</option>
                  <option value="none">Отключен (Только над рядами)</option>
                </select>
              </div>

              {/* Alignment */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', display: 'block', marginBottom: 5 }}>
                  Привязка к шкафам:
                </label>
                <select
                  value={alignment}
                  onChange={e => setAlignment(e.target.value as PathwayAlignment)}
                  style={{
                    background: 'var(--bg-panel, #0b1329)',
                    border: '1px solid var(--border-color, #334155)',
                    borderRadius: 6,
                    color: '#f8fafc',
                    padding: '8px 10px',
                    minHeight: 38,
                    fontSize: 11,
                    width: '100%',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="rear">Тыл шкафов (Rear — над вводами)</option>
                  <option value="centerline">Центральная ось (Centerline)</option>
                </select>
              </div>
            </div>

            {/* Overhang Slider / Input */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>
                  Консольный вынос за торец ряда (Overhang):
                </label>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#38bdf8', fontFamily: 'var(--font-data)' }}>
                  {overhangCm} см ({overhangCm / 100} м)
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={overhangCm}
                  onChange={e => setOverhangCm(Number(e.target.value))}
                  style={{ flex: 1, accentColor: '#0ea5e9', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0, 20, 30, 50].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setOverhangCm(v)}
                      style={{
                        padding: '2px 6px',
                        fontSize: 10,
                        borderRadius: 4,
                        border: '1px solid var(--border, #334155)',
                        background: overhangCm === v ? 'rgba(14, 165, 233, 0.2)' : 'transparent',
                        color: overhangCm === v ? '#38bdf8' : '#94a3b8',
                        cursor: 'pointer',
                      }}
                    >
                      {v}см
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Row Filter (Optional) */}
            {rowList.length > 1 && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', display: 'block', marginBottom: 4 }}>
                  Применить к рядам:
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedRowIds([])}
                    style={{
                      padding: '3px 8px',
                      fontSize: 10,
                      borderRadius: 4,
                      border: selectedRowIds.length === 0 ? '1px solid #0ea5e9' : '1px solid #334155',
                      background: selectedRowIds.length === 0 ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
                      color: selectedRowIds.length === 0 ? '#38bdf8' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: selectedRowIds.length === 0 ? 600 : 400,
                    }}
                  >
                    Все ряды ({rowList.length})
                  </button>
                  {rowList.map(r => {
                    const isChecked = selectedRowIds.includes(r.id);
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => handleRowToggle(r.id)}
                        style={{
                          padding: '3px 8px',
                          fontSize: 10,
                          borderRadius: 4,
                          border: isChecked ? '1px solid #0ea5e9' : '1px solid #334155',
                          background: isChecked ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
                          color: isChecked ? '#38bdf8' : '#94a3b8',
                          cursor: 'pointer',
                        }}
                      >
                        {r.name || `Ряд ${r.labelIndex}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Replace vs Merge option */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, color: '#e2e8f0' }}>
                <input
                  type="checkbox"
                  checked={replaceExisting}
                  onChange={e => setReplaceExisting(e.target.checked)}
                  style={{ accentColor: '#0ea5e9', width: 14, height: 14, cursor: 'pointer' }}
                />
                Заменить существующие лотки в зале
              </label>
              {existingTrayCount > 0 && (
                <span style={{ fontSize: 10, color: replaceExisting ? '#f59e0b' : '#94a3b8' }}>
                  {replaceExisting ? `Удалит ${existingTrayCount} старых сегментов` : `Объединит с ${existingTrayCount} старыми`}
                </span>
              )}
            </div>
          </div>

          {/* Section 3: Live Engineering Preview Box */}
          {preview && (
            <div
              style={{
                background: 'linear-gradient(145deg, #060e22, #0b152d)',
                border: '1px solid rgba(14, 165, 233, 0.25)',
                borderRadius: 8,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <CheckCircle2 size={13} color="#22c55e" />
                  Сводный инженерный расчет трасс
                </span>
                <span style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: preview.metrics.isFullyConnected ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                  color: preview.metrics.isFullyConnected ? '#22c55e' : '#f59e0b',
                  fontWeight: 600,
                  border: `1px solid ${preview.metrics.isFullyConnected ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                }}>
                  {preview.metrics.isFullyConnected ? '100% Связный граф' : `${preview.metrics.connectedComponents} изолированных зоны`}
                </span>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '6px 8px', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Всего длина трасс</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc', fontFamily: 'var(--font-data)' }}>
                    {preview.metrics.totalLengthM.toFixed(1)} м
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '6px 8px', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Узлов сети (Nodes)</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#f8fafc', fontFamily: 'var(--font-data)' }}>
                    {preview.metrics.totalNodes} шт.
                  </div>
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '6px 8px', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>Т-стыков (T-Junctions)</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#38bdf8', fontFamily: 'var(--font-data)' }}>
                    {preview.metrics.teeCount} шт.
                  </div>
                </div>
              </div>

              {/* Breakdown by Media */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 2 }}>
                {Object.entries(preview.metrics.lengthByMediaM).map(([media, len]) => (
                  <span
                    key={media}
                    style={{
                      fontSize: 10,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: media === 'fiber' ? '#fbbf24' : media === 'copper' ? '#38bdf8' : '#c084fc',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: media === 'fiber' ? '#fbbf24' : media === 'copper' ? '#38bdf8' : '#c084fc'
                    }} />
                    {media === 'fiber' ? 'Оптика' : media === 'copper' ? 'Медь' : 'Сила'}: <strong>{len.toFixed(1)} м</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Footer actions */}
        <div
          className="px-4 sm:px-5 py-3 border-t border-[var(--border,#1e293b)] flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-[var(--bg-card,#0f172a)] flex-shrink-0"
        >
          <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Info size={13} style={{ flexShrink: 0 }} />
            <span>Действие полностью обратимо через <strong>Ctrl+Z</strong></span>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                minHeight: 38,
                borderRadius: 6,
                border: '1px solid #334155',
                background: 'transparent',
                color: '#94a3b8',
                fontSize: 12,
                cursor: 'pointer',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Отмена
            </button>
            <button
              id="apply-pathways-btn"
              onClick={handleApply}
              disabled={!preview || rackList.length === 0}
              style={{
                padding: '8px 18px',
                minHeight: 38,
                borderRadius: 6,
                border: 'none',
                background: preview && rackList.length > 0 ? '#0284c7' : '#334155',
                color: '#ffffff',
                fontSize: 12,
                cursor: preview && rackList.length > 0 ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: preview && rackList.length > 0 ? '0 4px 12px rgba(2, 132, 199, 0.3)' : 'none',
                transition: 'background 0.15s ease, transform 0.05s ease',
              }}
              onMouseEnter={e => {
                if (preview && rackList.length > 0) e.currentTarget.style.background = '#0369a1';
              }}
              onMouseLeave={e => {
                if (preview && rackList.length > 0) e.currentTarget.style.background = '#0284c7';
              }}
            >
              <Check size={14} />
              <span>Применить магистрали</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
